import { useState, useEffect } from 'react';

interface NetworkStatus {
  isOnline: boolean;
  isOffline: boolean;
  connectionType?: string;
  effectiveType?: string;
}

const useNetworkStatus = (): NetworkStatus => {
  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>({
    isOnline: navigator.onLine,
    isOffline: !navigator.onLine,
  });

  useEffect(() => {
    const updateNetworkStatus = () => {
      const isOnline = navigator.onLine;

      setNetworkStatus({
        isOnline,
        isOffline: !isOnline,
        connectionType: (
          navigator as Navigator & {
            connection?: { type?: string; effectiveType?: string };
          }
        ).connection?.type,
        effectiveType: (
          navigator as Navigator & {
            connection?: { type?: string; effectiveType?: string };
          }
        ).connection?.effectiveType,
      });
    };

    // Set initial status
    updateNetworkStatus();

    // Add event listeners
    window.addEventListener('online', updateNetworkStatus);
    window.addEventListener('offline', updateNetworkStatus);

    // Listen for connection changes (if supported)
    if ('connection' in navigator) {
      (
        navigator as Navigator & {
          connection?: {
            addEventListener: (event: string, listener: () => void) => void;
          };
        }
      ).connection?.addEventListener('change', updateNetworkStatus);
    }

    return () => {
      window.removeEventListener('online', updateNetworkStatus);
      window.removeEventListener('offline', updateNetworkStatus);

      if ('connection' in navigator) {
        (
          navigator as Navigator & {
            connection?: {
              removeEventListener: (
                event: string,
                listener: () => void
              ) => void;
            };
          }
        ).connection?.removeEventListener('change', updateNetworkStatus);
      }
    };
  }, []);

  return networkStatus;
};

export default useNetworkStatus;
